import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import type { FeedFilterParams, GetUserPostsRequest, GetUserPostsResponse } from '../../types/feed';
import { withFeedTimezoneOffset } from './feedRequestPayload';

export type { FeedFilterParams as FilterPostsRequest } from '../../types/feed';

export interface GetFeedsRequest {
  method: string;
  startIndex: number;
  pageSize: number;
  timezoneOffset?: number;
  search?: string;
}

export interface GetPostRequest {
  postId: number;
  startIndex: number;
  pageSize: number;
  timezoneOffset?: number;
}

export interface FeedApiResponsePost {
  postId: number;
  profileUserGuid: string|null;
  groupId?: number | null;
  userId: number;
  userName: string;
  firstName?: string | null;
  groupName?: string | null;
  userPhoto?: string | null;
  postDate: string;
  contentTypeName?: string | null;
  contentTypeFor?: string | null;
  commentCount: number;
  reactedCount: number;
  isReacted: number;
  reactionName?: string | null;
  url?: string | null;
  isBuddy: number;
  isAIAgent: boolean;
  title?: string | null;
  bodyText?: string | null;
  currentWeight?: number | null;
  scaleUnitWeightName?: string | null;
  hashTags?: string[] | null;
  totalShares: number;
  allowComments: boolean;
  allowShare: boolean;
  invited: boolean;
  postVisibility: number;
  photoAlbum?: string[] | null;
  thumbnailUrl?: string | null;
  /** GetFeeds returns `{ profileUserGuid, username }` only (no numeric id). */
  taggedUsers?: Array<{
    profileUserGuid?: string | null;
    username?: string | null;
  }> | null;
  isSaved?: boolean;
  isBlocked?: boolean;
}

export interface GetFeedsResponse {
  statusCode: number;
  message: string;
  success: boolean;
  feeds: FeedApiResponsePost[];
}

export interface GetPostResponse {
  statusCode: number;
  message: string;
  success: boolean;
  feed: FeedApiResponsePost;
  comments: CommentApiResponse[];
}

export interface GetCommentsListRequest {
  postId: number;
  startIndex: number;
  pageSize: number;
  timezoneOffset?: number;
}

export interface CommentApiResponse {
  row: number;
  postCommentId: number;
  postId: number;
  /** null / 0 = top-level comment; otherwise postCommentId of parent */
  parentCommentId: number | null;
  userId: number;
  profileUserGuid?: string | null;
  userName: string;
  firstName?: string | null;
  userPhoto: string;
  isReacted: number | boolean;
  reactionName: string;
  bodyText: string;
  mediaUrl: string[];
  reactedCount: number;
  contentTypeFor: string;
  contentTypeName: string;
  commentDate: string;
  /** Whether the current user has blocked this comment author */
  isBlocked?: boolean;
  isAIAgent?: boolean;
}

export interface GetCommentsListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: CommentApiResponse[];
}

export interface SaveUnsavePostRequest {
  postId: number;
  isSave: boolean;
}

export interface SaveUnsavePostResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  isFollowing?: boolean;
}

export interface GetSavedPostsRequest {
  startIndex: number;
  pageSize: number;
  timezoneOffset?: number;
}

export interface GetSavedPostsResponse {
  statusCode: number;
  message: string;
  success: boolean;
  feeds: FeedApiResponsePost[];
}

export interface SharePostRequest {
  RootPostId: number;
  groupId?: number | null;
  privacyId?: number;
  bodyText?: string;
}

export interface SharePostResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
}

export interface ReportPostRequest {
  reportId?: number | null;
  postId?: number | null;
  commentId?: number | null;
  reasonIds?: number[];
  otherDescription?: string;
  reviewStatus: number;
}

export interface ReportPostResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
}

const feedApi = {
  getPost: (
    payload: GetPostRequest,
  ): Promise<AxiosResponse<GetPostResponse>> =>
    apiClient.post<GetPostResponse>(
      API_ENDPOINTS.FEEDS.GET_POST,
      withFeedTimezoneOffset(payload),
    ),
  getFeeds: (
    payload: GetFeedsRequest,
  ): Promise<AxiosResponse<GetFeedsResponse>> =>
    apiClient.post<GetFeedsResponse>(
      API_ENDPOINTS.FEEDS.GET_FEEDS,
      withFeedTimezoneOffset(payload),
    ),
  filterPosts: (
    payload: FeedFilterParams,
  ): Promise<AxiosResponse<GetFeedsResponse>> =>
    apiClient.post<GetFeedsResponse>(
      API_ENDPOINTS.FEEDS.FILTER_POSTS,
      payload,
    ),
  sharePost: (
    payload: SharePostRequest,
  ): Promise<AxiosResponse<SharePostResponse>> =>
    apiClient.post<SharePostResponse>(
      API_ENDPOINTS.FEEDS.SHARE_POST,
      payload,
    ),
  reportPost: (
    payload: ReportPostRequest,
  ): Promise<AxiosResponse<ReportPostResponse>> =>
    apiClient.post<ReportPostResponse>(
      API_ENDPOINTS.FEEDS.REPORT_POST,
      payload,
    ),
  saveUnsavePost: (
    payload: SaveUnsavePostRequest,
  ): Promise<AxiosResponse<SaveUnsavePostResponse>> =>
    apiClient.post<SaveUnsavePostResponse>(
      API_ENDPOINTS.FEEDS.SAVE_UNSAVE_POST,
      payload,
    ),
  getSavedPosts: (
    payload: GetSavedPostsRequest,
  ): Promise<AxiosResponse<GetSavedPostsResponse>> =>
    apiClient.post<GetSavedPostsResponse>(
      API_ENDPOINTS.FEEDS.GET_SAVED_POSTS,
      withFeedTimezoneOffset(payload),
    ),
  createPost: (
    payload: FormData,
  ): Promise<AxiosResponse<any>> =>
    apiClient.post<any>(
      API_ENDPOINTS.FEEDS.POST,
      payload,
    ),
  editPost: (
    payload: FormData,
  ): Promise<AxiosResponse<any>> =>
    apiClient.put<any>(
      API_ENDPOINTS.FEEDS.POST,
      payload,
    ),
  deletePost: (
    payload: { postId: number },
  ): Promise<AxiosResponse<any>> =>
    apiClient.delete<any>(API_ENDPOINTS.FEEDS.POST, {
      data: payload,
      headers: { 'Content-Type': 'application/json' },
    }),
  createComment: (
    payload: FormData,
  ): Promise<AxiosResponse<any>> =>
    apiClient.post<any>(API_ENDPOINTS.FEEDS.COMMENT, payload),
  editComment: (
    payload: FormData,
  ): Promise<AxiosResponse<any>> =>
    apiClient.put<any>(API_ENDPOINTS.FEEDS.COMMENT, payload),
  deleteComment: (
    postCommentId: number,
  ): Promise<AxiosResponse<any>> =>
    apiClient.delete<any>(
      API_ENDPOINTS.FEEDS.COMMENT,
      {
        data: { postCommentId },
      },
    ),
  getCommentsList: (
    payload: GetCommentsListRequest,
  ): Promise<AxiosResponse<GetCommentsListResponse>> =>
    apiClient.post<GetCommentsListResponse>(
      API_ENDPOINTS.FEEDS.GET_COMMENTS_LIST,
      withFeedTimezoneOffset(payload),
    ),
  getUserPosts: (
    payload: GetUserPostsRequest,
  ): Promise<AxiosResponse<GetUserPostsResponse>> =>
    apiClient.post<GetUserPostsResponse>(
      API_ENDPOINTS.FEEDS.GET_USER_POSTS,
      withFeedTimezoneOffset(payload),
    ),
  commentReaction: (
    payload: { postCommentId: number; reactionTypeId: number },
  ): Promise<AxiosResponse<any>> =>
    apiClient.post<any>(
      API_ENDPOINTS.FEEDS.COMMENT_REACTION,
      payload,
    ),
  postReaction: (
    payload: { postId: number; reactionTypeId: number },
  ): Promise<AxiosResponse<any>> =>
    apiClient.post<any>(
      API_ENDPOINTS.FEEDS.POST_REACTION,
      payload,
    ),
  getPostReactionList: (
    payload: GetPostReactionListRequest,
  ): Promise<AxiosResponse<GetPostReactionListResponse>> =>
    apiClient.post<GetPostReactionListResponse>(
      API_ENDPOINTS.FEEDS.GET_POST_REACTION_LIST,
      payload,
    ),
};

export interface GetPostReactionListRequest {
  postId: number;
  startIndex: number;
  pageSize: number;
  reactionTypeId?: number;
  timezoneOffset?: number;
}

export interface PostReactionUser {
  userId: number;
  userName: string;
  firstName?: string | null;
  userPhoto?: string | null;
  reactionTypeId: number;
  isBuddy?: boolean;
  /** Primary key on most feed endpoints. */
  profileUserGuid?: string | null;
  /** Alternate key returned by some endpoints (e.g. BuddySearch, LeaderBoard). */
  userGuid?: string | null;
  currentWeight?: number | string | null;
  weight?: number | string | null;
  scaleUnitWeightName?: string | null;
}

export interface GetPostReactionListResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  data?: PostReactionUser[];
  reactions?: PostReactionUser[];
}

export default feedApi;